import React, { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Drawer,
  Icon,
  Hidden,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import { ExitToApp } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { singleRouteNames, adminRouteNames, standardRouteNames } from 'routes';

import socket from 'config/socket';

import {
  logoutUser,
  addCustomerSocket,
  updateCustomerSocket,
  updateCustomerPayableSocket,
  addProductSocket,
  updateProductSocket,
  updateProductQuantitiesSocket,
  addExpenseSocket,
  addSalesOrderSocket,
  refundSalesOrderSocket,
  deleteSalesOrderSocket
} from 'redux/actions';
import { single, admin, standard } from 'variables/userAccountTypes';

import styles from 'assets/jss/material-dashboard-react/components/sidebarStyle.js';

const useStyles = makeStyles(styles);

function Sidebar({
  logoutUser,
  addCustomerSocket,
  updateCustomerSocket,
  updateCustomerPayableSocket,
  addProductSocket,
  updateProductSocket,
  updateProductQuantitiesSocket,
  addExpenseSocket,
  addSalesOrderSocket,
  refundSalesOrderSocket,
  deleteSalesOrderSocket,
  ...props }) {

  const classes = useStyles();
  let location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    //if authToken or activeUserId is not available, log the user out.
    if( !(props.authToken && props.activeUserId) ) {
      navigate('/login', { replace: true });
    }

  }, [props.authToken, props.activeUserId, navigate]);

  useEffect(() => {
    //only connect to socket if authToken or activeUserId is available and it is not a single user.
    if( props.authToken && props.activeUserId && props.activeUserType !== single ) {
      //only connect if the socket is not already connected
      if(!socket.connected) {
        //add the token to the socket object
        socket.auth = {
          token: props.authToken
        };

        //connect to the socket server
        socket.connect();
      }

      //socket event listeners
      socket.on('add-customer-broad', data => {
        addCustomerSocket(data);
      });

      socket.on('update-customer-broad', data => {
        updateCustomerSocket(data);
      });

      socket.on('update-customer-payable-broad', data => {
        updateCustomerPayableSocket(data);
      });

      socket.on('add-product-broad', data => {
        addProductSocket(data);
      });

      socket.on('update-product-broad', data => {
        updateProductSocket(data);
      });

      socket.on('update-product-quant-broad', data => {
        updateProductQuantitiesSocket(data);
      });
      
      socket.on('add-expense', data => {
        addExpenseSocket(data);
      });
      
      socket.on('add-sales-order', data => {
        addSalesOrderSocket(data);
      });
      
      socket.on('refund-sales-order', data => {
        refundSalesOrderSocket(data);
      });
      
      socket.on('delete-sales-order', data => {
        deleteSalesOrderSocket(data);
      });
    }

  }, [
    props.activeUserId,
    props.authToken,
    props.activeUserType,
    addCustomerSocket,
    updateCustomerSocket,
    updateCustomerPayableSocket,
    addProductSocket,
    updateProductSocket,
    updateProductQuantitiesSocket,
    addExpenseSocket,
    addSalesOrderSocket,
    refundSalesOrderSocket,
    deleteSalesOrderSocket
  ]);

  const handleLogout = () => {
    if(props.activeUserType !== single && socket.connected) {
      socket.emit('leave-rooms');
      socket.removeAllListeners();
    }

    logoutUser();
    navigate('/login', { replace: true });
  };

  // verifies if routeName is the one active (in browser input)
  function activeRoute(routeName) {
    return location.pathname === routeName;
  };

  const setRouteNames = () => {
    switch(props.activeUserType) {
      case single:
        return singleRouteNames;
      case admin:
        return adminRouteNames;
      case standard:
        return standardRouteNames;
      default:
        return singleRouteNames;
    }
  };

  const currentRouteNames = setRouteNames();

  const { color, logoText } = props;
  var links = (
    <List className={classes.list}>
      {currentRouteNames.map((prop, key) => {
        var listItemClasses;
          listItemClasses = classNames({
            [" " + classes[color]]: activeRoute(prop.href),
          });
        const whiteFontClasses = classNames({
          [" " + classes.whiteFont]: activeRoute(prop.href),
        });
        return (
          <NavLink
            to={prop.href}
            className={classes.item}
            key={key}
          >
            <ListItem button className={classes.itemLink + listItemClasses}>
              {/*Determines whether the icon of the component is a string.
              Done for a single use case 'table'*/}
              {typeof prop.icon === "string" ? (
                <Icon
                  className={classNames(classes.itemIcon, whiteFontClasses)}
                >
                  {prop.icon}
                </Icon>
              ) : (
                <prop.icon
                  className={classNames(classes.itemIcon, whiteFontClasses)}
                />
              )}
              <ListItemText
                primary={prop.name}
                className={classNames(classes.itemText, whiteFontClasses)}
                disableTypography={true}
              />
            </ListItem>
          </NavLink>
        );
      })}
      <ListItem
        button
        className={classes.itemLinkLogout}
        onClick={handleLogout}
      >
        <ExitToApp
          className={classNames(classes.itemIcon)}
        />
        <ListItemText
          primary={"Logout"}
          className={classNames(classes.itemText)}
          disableTypography={true}
        />
      </ListItem>
    </List>
  );

  var brand = (
    <div className={classes.logo}>
        {/* eslint-disable-next-line*/}
        <a className={classes.logoLink}>
        {logoText}
        </a>
    </div>
  );

  return (
    <div>
      <Hidden smDown implementation="css">
        <Drawer
          anchor="left"
          variant="permanent"
          open
          classes={{ paper: classNames(classes.drawerPaper) }}
        >
          {brand}
          <div className={classes.sidebarWrapper}>{ links }</div>
          <div className={classes.background} />
        </Drawer>
      </Hidden>
    </div>
  );
};

export default connect (null, {
  logoutUser,
  addCustomerSocket,
  updateCustomerSocket,
  updateCustomerPayableSocket,
  addProductSocket,
  updateProductSocket,
  updateProductQuantitiesSocket,
  addExpenseSocket,
  addSalesOrderSocket,
  refundSalesOrderSocket,
  deleteSalesOrderSocket
}) (Sidebar);

Sidebar.propTypes = {
  handleDrawerToggle: PropTypes.func,
  bgColor: PropTypes.oneOf(["purple", "blue", "green", "orange", "red"]),
  image: PropTypes.string,
  logoText: PropTypes.string,
  open: PropTypes.bool,
};